import React, { useEffect, useState } from "react";
import MasjidPicker from "./MasjidPicker";
import AudioChoicePicker from "./AudioChoicePicker";
import WordingSettings from "./WordingSettings";
import AzanOptions from "./AzanOptions";
import LicenceForm from "./LicenceForm";
import { HomeLicenceState } from "./homeLicence";
import { toHomeMasjid } from "./masjidIndex";
import {
  HomeMasjid,
  HomeSettings,
  MAX_FAVOURITES,
  audioChoiceFor,
  azanSettingsFor,
} from "./homeSettings";
import { canWakeScreen } from "./useWakeSchedule";
import { TvPosition, canTurnTv, isTv } from "./nativeApp";
import { fetchScreenData } from "../../services/api";
import { HomeScreenChoice } from "../../types/ScreenTypes";

const WAKE_MINUTES = [0, 5, 10, 15, 30];

// Each favourite's own screens (the ones its masjid ticked for homes), from its saved "home" screen.
const useMasjidScreens = (favourites: HomeMasjid[], enabled: boolean) => {
  const [screens, setScreens] = useState<Record<number, HomeScreenChoice[]>>({});
  const ids = favourites.map((m) => m.id).join(",");
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    for (const masjid of favourites) {
      fetchScreenData(masjid.id, "home")
        .then((screen) => {
          if (!cancelled) setScreens((all) => ({ ...all, [masjid.id]: screen.homeScreens ?? [] }));
        })
        .catch(() => {});
    }
    return () => {
      cancelled = true;
    };
  }, [ids, enabled]); // eslint-disable-line react-hooks/exhaustive-deps
  return screens;
};

// Which way up shows a masjid screen full size. A TV stays as it's fixed to the wall.
const shapeNote = (orientation: HomeScreenChoice["orientation"], tvPosition?: TvPosition) => {
  const portrait = orientation === "portrait";
  const label = portrait ? "Portrait" : "Landscape";
  if (!isTv()) {
    return `${label}: fills the screen when the tablet is ${portrait ? "upright" : "on its side"}.`;
  }
  const tvUpright = !!tvPosition && tvPosition !== "normal";
  return portrait === tvUpright ? `${label}: fills the TV.` : `${label}: shows in the middle of the TV.`;
};

const TV_POSITIONS: { position: TvPosition; title: string; detail: string }[] = [
  { position: "normal", title: "Normal", detail: "The TV stands the usual way." },
  { position: "left", title: "On its side, turned left", detail: "The TV's top edge is on the left." },
  { position: "right", title: "On its side, turned right", detail: "The TV's top edge is on the right." },
];

interface SettingsPanelProps {
  settings: HomeSettings;
  licence: HomeLicenceState;
  onChange: (settings: HomeSettings) => void;
  onReset: () => void;
  onClose: () => void;
}

const SettingsPanel: React.FC<SettingsPanelProps> = ({
  settings,
  licence,
  onChange,
  onReset,
  onClose,
}) => {
  const [adding, setAdding] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [removeFailed, setRemoveFailed] = useState(false);
  const { favourites } = settings;
  const masjidScreens = useMasjidScreens(favourites, licence.licensed);

  const pickScreen = (masjidId: number, screenId?: number) => {
    const picks = { ...settings.masjidScreens };
    if (screenId) picks[masjidId] = screenId;
    else delete picks[masjidId];
    onChange({ ...settings, masjidScreens: picks });
  };

  const makeHome = (id: number) =>
    onChange({
      ...settings,
      favourites: [
        ...favourites.filter((m) => m.id === id),
        ...favourites.filter((m) => m.id !== id),
      ],
    });

  const remove = (id: number) =>
    onChange({ ...settings, favourites: favourites.filter((m) => m.id !== id) });

  const removeTablet = async () => {
    setRemoving(true);
    setRemoveFailed(false);
    const removed = await licence.remove();
    setRemoving(false);
    setConfirmRemove(false);
    setRemoveFailed(!removed);
  };

  return (
    <div className="hs-sheet" role="dialog" aria-modal="true" aria-label="Settings">
      <header className="hs-sheet-head">
        <h1>Settings</h1>
        <button className="hs-close" onClick={onClose} aria-label="Close">
          ✕
        </button>
      </header>
      <div className="hs-sheet-body">
        <h2>Subscription</h2>
        {licence.licensed && licence.licence ? (
          <>
            <p className="hs-note">
              Active on this tablet, with the code ending {licence.licence.key.slice(-6)}.
            </p>
            {confirmRemove ? (
              <>
                <p className="hs-note">
                  This frees the code for another tablet, and this one goes back to the preview.
                </p>
                <div className="hs-actions">
                  <button className="hs-button" onClick={() => setConfirmRemove(false)}>
                    Keep it here
                  </button>
                  <button className="hs-button hs-danger" onClick={removeTablet} disabled={removing}>
                    {removing ? "Removing…" : "Yes, remove this tablet"}
                  </button>
                </div>
              </>
            ) : (
              <button className="hs-button" onClick={() => setConfirmRemove(true)}>
                Remove this tablet…
              </button>
            )}
            {removeFailed && (
              <p className="hs-problem" role="alert">
                Removing the tablet needs the internet. Check the Wi-Fi and try again.
              </p>
            )}
          </>
        ) : (
          <>
            <p className="hs-note">
              {licence.licence?.ended
                ? "This tablet's code no longer works: the subscription may have ended, or the tablet was removed from the code. "
                : licence.licence
                  ? "This tablet hasn't been able to check its code for 30 days, so it's showing the preview. Connect it to the internet, or enter the code again. "
                  : `This tablet is showing the preview: ${favourites[0].name}'s prayer times only. `}
              With a code it also shows posters and notices, plays the azan, starts live audio by
              itself, holds up to {MAX_FAVOURITES} masjids and keeps working without the internet.
            </p>
            <LicenceForm activate={licence.activate} />
          </>
        )}

        <h2>Your masjids</h2>
        <p className="hs-note">
          The first is your home masjid: the tablet returns to it after 5 minutes.
        </p>
        <ol className="hs-favourites">
          {favourites.map((masjid, i) => (
            <li key={masjid.id} className="hs-favourite">
              <div className="hs-favourite-head">
                <span>
                  <strong>{masjid.name}</strong>
                  {i === 0 && <span className="hs-pill">Home</span>}
                  {masjid.town && <span className="hs-row-town"> {masjid.town}</span>}
                </span>
                <span className="hs-favourite-buttons">
                  {i > 0 && (
                    <button className="hs-button" onClick={() => makeHome(masjid.id)}>
                      Make home
                    </button>
                  )}
                  {favourites.length > 1 && (
                    <button className="hs-button" onClick={() => remove(masjid.id)}>
                      Remove
                    </button>
                  )}
                </span>
              </div>
              {masjid.audio && (
                <details className="hs-audio-setting">
                  <summary>
                    When it goes live:{" "}
                    {
                      {
                        auto: "play automatically",
                        ask: "show a Listen button",
                        off: "do nothing",
                      }[audioChoiceFor(settings, masjid)]
                    }
                  </summary>
                  <AudioChoicePicker
                    masjid={masjid}
                    isHome={i === 0}
                    value={audioChoiceFor(settings, masjid)}
                    onChange={(choice) =>
                      onChange({ ...settings, audio: { ...settings.audio, [masjid.id]: choice } })
                    }
                  />
                </details>
              )}
            </li>
          ))}
        </ol>

        {favourites.length < MAX_FAVOURITES &&
          (adding ? (
            <MasjidPicker
              autoFocus
              added={favourites.map((m) => m.id)}
              onPick={(masjid) => {
                onChange({ ...settings, favourites: [...favourites, toHomeMasjid(masjid)] });
                setAdding(false);
              }}
            />
          ) : (
            <button className="hs-button" onClick={() => setAdding(true)}>
              ＋ Add a masjid
            </button>
          ))}

        <h2>Look</h2>
        <div className="hs-choices" role="radiogroup" aria-label="Look">
          {[
            { look: "colour" as const, title: "Colour", detail: "Your masjid's colour on a dark screen." },
            {
              look: "mono" as const,
              title: "Black and white",
              detail: "Black on white, like the Masjid Clock. Clear in a bright room.",
            },
          ].map((option) => {
            const active = (settings.look ?? "colour") === option.look;
            return (
              <button
                key={option.look}
                role="radio"
                aria-checked={active}
                className={`hs-choice${active ? " active" : ""}`}
                onClick={() => onChange({ ...settings, look: option.look })}
              >
                <strong>{option.title}</strong>
                <span>{option.detail}</span>
              </button>
            );
          })}
        </div>

        <h2>Screen</h2>
        <div className="hs-choices" role="radiogroup" aria-label="Screen">
          {[
            {
              style: "simple" as const,
              title: "Prayer times, posters on tap",
              detail: "A calm screen. Tap Posters to see the masjid's posters and notices.",
            },
            {
              style: "next" as const,
              title: "Next prayer, posters on tap",
              detail: "Just the next salah, like the Masjid Clock: when it begins, its jama'ah and a countdown.",
            },
            {
              style: "notices" as const,
              title: "Posters on the screen",
              detail: "The masjid's posters, du'as and notices change by themselves alongside the times.",
            },
          ].map((option) => {
            const active = (settings.screenStyle ?? "simple") === option.style;
            return (
              <button
                key={option.style}
                role="radio"
                aria-checked={active}
                className={`hs-choice${active ? " active" : ""}`}
                onClick={() => onChange({ ...settings, screenStyle: option.style })}
              >
                <strong>{option.title}</strong>
                <span>{option.detail}</span>
              </button>
            );
          })}
        </div>
        {favourites.map((masjid) => {
          const choices = masjidScreens[masjid.id];
          if (!choices?.length) return null;
          const picked = choices.find((c) => c.id === settings.masjidScreens?.[masjid.id])?.id;
          return (
            <React.Fragment key={masjid.id}>
              <h3>{masjid.name}'s own screens</h3>
              <div
                className="hs-choices hs-choices-two"
                role="radiogroup"
                aria-label={`${masjid.name}'s own screens`}
              >
                <button
                  role="radio"
                  aria-checked={!picked}
                  className={`hs-choice${!picked ? " active" : ""}`}
                  onClick={() => pickScreen(masjid.id)}
                >
                  <strong>Home's layout</strong>
                  <span>As chosen above.</span>
                </button>
                {choices.map((choice) => (
                  <button
                    key={choice.id}
                    role="radio"
                    aria-checked={picked === choice.id}
                    className={`hs-choice${picked === choice.id ? " active" : ""}`}
                    onClick={() => pickScreen(masjid.id, choice.id)}
                  >
                    <strong>
                      <span className={`hs-shape hs-shape-${choice.orientation}`} />
                      {choice.name}
                    </strong>
                    <span>{shapeNote(choice.orientation, settings.tvPosition)}</span>
                  </button>
                ))}
              </div>
            </React.Fragment>
          );
        })}

        {canTurnTv() && (
          <>
            <h2>TV position</h2>
            <p className="hs-note">
              For a TV fixed to the wall on its side, so portrait screens show full size.
            </p>
            <div className="hs-choices" role="radiogroup" aria-label="TV position">
              {TV_POSITIONS.map((option) => {
                const active = (settings.tvPosition ?? "normal") === option.position;
                return (
                  <button
                    key={option.position}
                    role="radio"
                    aria-checked={active}
                    className={`hs-choice${active ? " active" : ""}`}
                    onClick={() => onChange({ ...settings, tvPosition: option.position })}
                  >
                    <strong>
                      <span
                        className={`hs-shape hs-shape-${option.position === "normal" ? "landscape" : "portrait"}`}
                      />
                      {option.title}
                    </strong>
                    <span>{option.detail}</span>
                  </button>
                );
              })}
            </div>
            <p className="hs-note">
              The TV's own keyboard and volume pop-up stay sideways: the TV draws those, not Home.
            </p>
          </>
        )}

        <h2>Azan</h2>
        <p className="hs-note">At {favourites[0].name}'s prayer times, on this tablet.</p>
        <AzanOptions
          azan={azanSettingsFor(settings)}
          jamaatWord={settings.wording?.jamaat || "Jama'ah"}
          onChange={(azan) => onChange({ ...settings, azan })}
        />

        {canWakeScreen() && (
          <>
            <h2>When the screen is off</h2>
            <p className="hs-note">
              If the screen has been switched off, it still comes on for the azan and when{" "}
              {favourites[0].name} goes live. It can also come on before each salah, then go off
              again about 5 minutes after.
            </p>
            <div className="hs-wording">
              {[
                {
                  key: "begins" as const,
                  label: "Before each begins time (sunrise and Sehri end too)",
                },
                {
                  key: "jamaah" as const,
                  label: `Before each ${settings.wording?.jamaat || "Jama'ah"}`,
                },
              ].map((option) => (
                <label key={option.key} className="hs-field">
                  <span>{option.label}</span>
                  <select
                    className="hs-select"
                    value={settings.wake?.[option.key] ?? 0}
                    onChange={(e) =>
                      onChange({
                        ...settings,
                        wake: {
                          begins: 0,
                          jamaah: 0,
                          ...settings.wake,
                          [option.key]: Number(e.target.value),
                        },
                      })
                    }
                  >
                    {WAKE_MINUTES.map((m) => (
                      <option key={m} value={m}>
                        {m === 0 ? "Off" : `${m} min before`}
                      </option>
                    ))}
                  </select>
                </label>
              ))}
            </div>
          </>
        )}

        <h2>Wording</h2>
        <p className="hs-note">Used on every masjid's screen on this tablet.</p>
        <WordingSettings
          wording={settings.wording ?? {}}
          onChange={(wording) => onChange({ ...settings, wording })}
        />

        <h2>Start again</h2>
        <p className="hs-note">Clears this tablet's masjids and choices, then runs the setup again.</p>
        {confirmReset ? (
          <div className="hs-actions">
            <button className="hs-button" onClick={() => setConfirmReset(false)}>
              Keep my settings
            </button>
            <button className="hs-button hs-danger" onClick={onReset}>
              Yes, start again
            </button>
          </div>
        ) : (
          <button className="hs-button" onClick={() => setConfirmReset(true)}>
            Start again…
          </button>
        )}
      </div>
    </div>
  );
};

export default SettingsPanel;
